import qs from "qs"
import axios, { AxiosHeaderValue, AxiosResponse } from "axios"
import * as Sentry from "@sentry/vue"
import axiosInstance from "@/api/axios"
import ApiStatusError from "@/api/errors/ApiStatusError"
import { z } from "zod/v4"
import errorTypeConst from "@common_lib/base/constants/errorTypeConst"
import extractLastSegment from "@/utils/extractLastSegment"

const REQUEST_CANCELED_MESSAGE_PREFIX = "request canceled for "

// Experiment (single-use jsessionid): each queryPass value travels as a URL
// `;jsessionid=` param exactly once — the backend must adopt the session and
// re-issue the JSESSIONID cookie on that first response, because every later
// request carries the session cookie only. A queryPass counts as spent once a
// response arrives (whatever its status); a network-level failure keeps the
// retry carrying the session id.
let spentQueryPass: AxiosHeaderValue | undefined

export const isCanceledRequestError = (error: unknown): boolean =>
  error instanceof Error && error.message.startsWith(REQUEST_CANCELED_MESSAGE_PREFIX)

export const ERROR_TYPE_CONST = {
  IP_BLOCK: "1002",
  IPS_BLOCK: "1009",
  INSUFFICIENT_PARAMETER: "3000"
}

// Failure responses carry their reason in status_msg (status-based endpoints) or in
// error (the rest). Endpoint-specific fields stay on ApiStatusError.data for the
// owning service to parse into a subclass.
const failureResponseSchema = z.object({
  status: z.string().optional(),
  status_msg: z.string().optional(),
  error: z.string().optional()
})

const toApiStatusError = (url: string, responseBody: unknown) => {
  const parsed = failureResponseSchema.safeParse(responseBody)
  const body: z.infer<typeof failureResponseSchema> = parsed.success ? parsed.data : {}

  return new ApiStatusError({
    url,
    status: body.status,
    message: body.status_msg ?? body.error ?? "",
    data: responseBody
  })
}

interface FetchAPIOptions {
  data?: object
  // the form parameters the endpoint reads; see src/schemas/requestParams.ts
  requestSchema: z.ZodType
  method?: "post" | "get"
  url: string
  qsOptions?: object
  withCredentials?: boolean
  hasStatus?: boolean
  [key: string]: unknown
}

const reportedSchemaMismatches = new Set<string>()

// ["events", 0, "markets", 3, "status"] → "events[].markets[].status"
const formatPath = (path: PropertyKey[]) =>
  path.reduce<string>((out, key) => {
    if (typeof key === "number") return `${out}[]`
    return out ? `${out}.${String(key)}` : String(key)
  }, "")

// A union only reports "Invalid input" at its own path; the reason sits in each branch's
// issues, so expand them (with the union's path in front) to say which field failed.
const describeIssues = (
  issues: z.ZodError["issues"],
  basePath: PropertyKey[] = []
): string[] =>
  issues.flatMap(issue => {
    const path = [...basePath, ...issue.path]
    if (issue.code === "invalid_union" && issue.errors.length > 0) {
      return issue.errors.flatMap(branchIssues => describeIssues(branchIssues, path))
    }
    return [`${formatPath(path) || "(root)"} (${issue.code}): ${issue.message}`]
  })

// Report-only: a mismatch means the schema or the caller is wrong about the real API, and
// blocking on it would turn a schema mistake into an outage, so the request is still sent and
// the response still returned. Once per endpoint and direction per page load, with array indices
// collapsed (events[].markets[].status), and only paths and codes — values may hold player and
// bet data.
const reportSchemaMismatch = (
  direction: "request" | "response",
  url: string,
  error: z.ZodError
) => {
  const key = `${direction} ${url}`
  if (reportedSchemaMismatches.has(key)) return
  reportedSchemaMismatches.add(key)

  const issues = [...new Set(describeIssues(error.issues))]
  console.warn(`[fetchAPI] ${direction} does not match schema: ${url}`, issues)
  Sentry.captureMessage(`[fetchAPI] ${direction} does not match schema`, {
    level: "warning",
    tags: { source: "fetchAPI", url, direction },
    extra: { issues: issues.slice(0, 50) }
  })
}

// `schema` describes the success response and gives the call its type. It only validates:
// the raw response is returned as-is, so schemas must not transform (no default/transform).
// `requestSchema` describes the parameters the endpoint reads and is checked before sending;
// a mismatch is reported the same way and the request still goes out.
export const fetchAPI = async <S extends z.ZodType>({
  data,
  requestSchema,
  method,
  url,
  qsOptions,
  withCredentials,
  hasStatus,
  schema,
  ...rest
}: FetchAPIOptions & { schema: S }): Promise<z.output<S>> => {
  const queryPass = axiosInstance.defaults.headers.common.Authorization
  const isFirstUseOfQueryPass = Boolean(queryPass) && queryPass !== spentQueryPass
  const formData = qs.stringify({ ...data }, qsOptions)
  // What the backend actually receives: qs.stringify drops undefined, and RequestParser treats
  // an empty value as missing (null and "" both stringify to "").
  const sentData = Object.fromEntries(
    Object.entries({ ...data }).filter(
      ([, paramValue]) =>
        paramValue !== undefined && paramValue !== null && paramValue !== ""
    )
  )
  const parsedRequest = requestSchema.safeParse(sentData)

  if (!parsedRequest.success) reportSchemaMismatch("request", url, parsedRequest.error)
  const websiteInfoStore = useWebsiteInfoStore()

  try {
    const res = await axiosInstance({
      ...rest,
      withCredentials: withCredentials !== false,
      method: method ?? "post",
      data: formData,
      url: isFirstUseOfQueryPass ? `${url};jsessionid=${queryPass}` : url
    })

    if (isFirstUseOfQueryPass) {
      spentQueryPass = queryPass
    }

    const { status } = res.data

    if (status === ERROR_TYPE_CONST.IP_BLOCK || status === ERROR_TYPE_CONST.IPS_BLOCK) {
      // The block reason (blocked IP / ISP, ban country, login-failure auto block)
      // is only in status_msg, which becomes the error message.
      const blockError = toApiStatusError(url, res.data)
      websiteInfoStore.updateWebsiteErrorStatus("IPBlock", blockError.message)
      return Promise.reject(blockError)
    }

    if (status === errorTypeConst.SYSTEM_MAINTENANCE.status) {
      websiteInfoStore.updateWebsiteErrorStatus("SystemMaintenance")
      return Promise.reject(toApiStatusError(url, res.data))
    }

    if (
      status === errorTypeConst.NOT_AUTHORIZED.status ||
      status === errorTypeConst.USER_IS_NULL.status ||
      status === errorTypeConst.SESSION_TIMEOUT.status ||
      (url === "/member/playerService/queryPlayerInfo" &&
        status === errorTypeConst.FAIL.status)
    ) {
      return Promise.reject(errorTypeConst.NOT_AUTHORIZED.status)
    }
    if (status === ERROR_TYPE_CONST.INSUFFICIENT_PARAMETER) {
      return Promise.reject(toApiStatusError(url, res.data))
    }
    if (status === errorTypeConst.CASINO_MAINTENANCE.status) {
      return Promise.reject(errorTypeConst.CASINO_MAINTENANCE.message)
    }
    // Must stay `return Promise.reject(...)`: a `throw` here would land in the catch below
    // and be re-wrapped as a plain Error, losing ApiStatusError for every caller.
    if (res.data.error) {
      return Promise.reject(toApiStatusError(url, res.data))
    }
    if (hasStatus && status !== "0000") {
      return Promise.reject(toApiStatusError(url, res.data))
    }

    const parsed = schema.safeParse(res.data)

    if (!parsed.success) reportSchemaMismatch("response", url, parsed.error)

    return Promise.resolve(res.data)
  } catch (error) {
    if (axios.isCancel(error)) {
      const message = `${REQUEST_CANCELED_MESSAGE_PREFIX}${extractLastSegment(url)}`
      console.warn(message)
      throw new Error(message)
    }

    const err = error as { response: AxiosResponse }

    if (
      err.response &&
      url === "/member/playerService/queryPlayerInfo" &&
      err.response.status === 410 &&
      err.response?.data?.status === errorTypeConst.FAIL.status
    ) {
      console.warn(err.response.data.status_msg)
      return Promise.reject(errorTypeConst.NOT_AUTHORIZED.status)
    }

    throw new Error(`${error}`)
  }
}

export default fetchAPI
